"use client"

import React from "react"

import { useTestSetRecordsTransport } from "@/test-set/transport"
import {
  Checkbox,
  ChevronDownIcon,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  selectTriggerVariants,
  Skeleton,
} from "@coval/ui"

const PREVIEW_TRUNCATE_LENGTH = 80

interface TestCasePickerProps {
  testSetId: string
  selectedTestCaseIds: string[]
  onSelectedTestCaseIdsChange: (ids: string[]) => void
  disabled?: boolean
}

export function TestCasePicker({
  testSetId,
  selectedTestCaseIds,
  onSelectedTestCaseIdsChange,
  disabled = false,
}: TestCasePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const { data: records, isLoading } = useTestSetRecordsTransport(
    { test_set_id: testSetId },
    { enabled: Boolean(testSetId) },
  )

  const filteredRecords = React.useMemo(() => {
    if (!records) {
      return []
    }
    if (!searchQuery) {
      return records
    }
    const q = searchQuery.toLowerCase()
    return records.filter(
      (r) =>
        r.input_str?.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q),
    )
  }, [records, searchQuery])

  const handleToggle = React.useCallback(
    (id: string) => {
      if (selectedTestCaseIds.includes(id)) {
        onSelectedTestCaseIdsChange(selectedTestCaseIds.filter((i) => i !== id))
      } else {
        onSelectedTestCaseIdsChange([...selectedTestCaseIds, id])
      }
    },
    [selectedTestCaseIds, onSelectedTestCaseIdsChange],
  )

  const handleSelectAllFiltered = React.useCallback(() => {
    const filteredIds = filteredRecords.map((r) => r.id)
    const merged = new Set([...selectedTestCaseIds, ...filteredIds])
    onSelectedTestCaseIdsChange(Array.from(merged))
  }, [filteredRecords, selectedTestCaseIds, onSelectedTestCaseIdsChange])

  const handleClearAll = React.useCallback(() => {
    onSelectedTestCaseIdsChange([])
  }, [onSelectedTestCaseIdsChange])

  React.useEffect(() => {
    if (disabled) {
      setOpen(false)
      setSearchQuery("")
    }
  }, [disabled])

  const hasSelection = selectedTestCaseIds.length > 0

  const triggerLabel = hasSelection
    ? `${selectedTestCaseIds.length} test case${selectedTestCaseIds.length === 1 ? "" : "s"} selected`
    : null

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled && next) {
          return
        }
        setOpen(next)
        if (!next) {
          setSearchQuery("")
        }
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            selectTriggerVariants(),
            "focus:outline-none disabled:pointer-events-none",
          )}
        >
          <span className="truncate">
            {triggerLabel ?? (
              <span className="text-coval-text-muted">
                Select test cases...
              </span>
            )}
          </span>
          <ChevronDownIcon />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="min-w-[var(--radix-popover-trigger-width)] p-1"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          searchInputRef.current?.focus()
        }}
      >
        <div className="flex items-center gap-2 px-1 pb-1">
          <SearchInput
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            placeholder="Filter by input or ID..."
            aria-label="Filter test cases"
            className="flex-1"
          />
          <button
            type="button"
            onClick={handleSelectAllFiltered}
            disabled={filteredRecords.length === 0}
            aria-label="Select all test cases"
            className="text-xs text-coval-info-text hover:opacity-80 whitespace-nowrap disabled:opacity-50"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            disabled={!hasSelection}
            aria-label="Clear selection"
            className="text-xs text-coval-danger-text hover:opacity-80 whitespace-nowrap disabled:opacity-50"
          >
            Clear
          </button>
        </div>

        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center gap-2 px-2 py-4">
              <Skeleton className="h-4 w-16 rounded-md" />
              <span className="text-xs text-coval-text-muted">
                Loading test cases...
              </span>
            </div>
          ) : filteredRecords.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
              {searchQuery
                ? "No test cases match your filter"
                : "No test cases in this test set"}
            </p>
          ) : (
            filteredRecords.map((record) => {
              const isSelected = selectedTestCaseIds.includes(record.id)
              const preview =
                record.description && record.description.length > 0
                  ? record.description.slice(0, PREVIEW_TRUNCATE_LENGTH)
                  : record.input_str &&
                      record.input_str.length > PREVIEW_TRUNCATE_LENGTH
                    ? record.input_str.slice(0, PREVIEW_TRUNCATE_LENGTH)
                    : record.description || record.input_str || "(empty)"

              return (
                <label
                  key={record.id}
                  className="flex w-full cursor-pointer select-none items-start gap-2 rounded-sm px-2 py-1.5 text-sm text-coval-text hover:bg-surface-default-hover"
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(record.id)}
                    aria-label={preview}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{preview}</p>
                    <p className="truncate text-xs text-coval-text-muted font-mono">
                      {record.id}
                    </p>
                  </div>
                </label>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
